import React, { useState, useEffect, FC, useCallback } from "react";
import { Row, Col, Button } from "reactstrap";
import { Row as RRow, Col as RCol, } from "reactstrap";
import {
  TableColumnsType,
  Table,
  Input,
  Tooltip,
  Modal,
  Space,
  Tag,
  Descriptions,
  Timeline,
  Divider,
  Button as AntButton,
  Statistic,
  Form,
  Popover,
  Flex,
  Spin,
  Drawer,
  Select,
  Badge,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import PaymentsAside from "./PaymentsAside";
import ReceivablesAside from "./ReceivablesAside";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import { PaymentData, PaymentStatus, UserRole } from "./types";
import { getPaymentsForApproval } from "./mockData/paymentData";
import paymentApis from "../../api/WorkOrderManagement/PaymentsApis";
import { Icon } from "../../components/Component";
import SignatureModal from "../components/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

// Filter State Interface
interface FilterState {
  siteId?: string[];
  contractorName?: string[];
  workOrderId?: string[];
  raBillId?: string[];
}

const PendingPaymentApprovals: FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const flowType = searchParams.get("flowType");
  const navigate = useNavigate();

  const [sm, setSm] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false);

  // Payment approvals state
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<"approve" | "reject">(
    "approve"
  );
  const [approvalComments, setApprovalComments] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const { data: currentUser }: any = useSelector(
    (state: any) => state.currentUser
  );
  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const [approverIds, setApproverIds] = useState<any>([]);
  const [isStrictMode, setIsStrictMode] = useState<boolean>(false);

  const [signature, setSignature] = useState({
    isActive: false,
    label: "Authorised Signatory",
    image: null as string | null,
  });
  const [showSignatureModel, setShowSignatureModel] = useState(false);
  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    "APPROVE" | "REJECT" | null
  >(null);
  const [form] = Form.useForm();

  const [currentUserRole] = useState<UserRole>(UserRole.Approver);

  useEffect(() => {
    if (currentUser) {
      setCurrentUserId(currentUser.id);
    }
  }, [currentUser]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };
  // Advanced Filters State
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [filterForm] = Form.useForm();
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<any[]>([]);
  const [raBillOptions, setRABillOptions] = useState<any[]>([]);
  const isClientFlow = flowType?.toLowerCase() === "client";

  // Load payments on component mount and refresh
  useEffect(() => {
    loadPaymentsForApproval();
  }, [currentUserRole, refreshToggle]);

  // Fetch site options from currentUser
  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  const handleViewPayment = async (payment: any) => {
    try {
      setLoading(true);

      // Call the paymentById API with the raBillPaymentId
      const response: any = await paymentApis.paymentById(
        payment.raBillPaymentId
      );

      console.log("Payment details response:", response);

      const { data, status, message } = response;

      if (status) {
        // Process the API response and map it to the expected payment structure
        const processedPayment = {
          ...payment,
          // Map API response fields to payment object
          paymentVoucherNumber: data.raBillPaymentSequenceId,
          paymentDate: data.paymentDate,
          paymentType: data.paymentType,
          status: data.status,
          contractorName: data.contractorName,
          siteName: data.siteName,
          paymentMethod: data.paymentMethod,
          bankAccountNumber: data.bankAccountNumber,
          bankName: data.bankName,
          totalPaymentAmount: data.totalPaymentAmount,
          paidAmount: data.paidAmount,
          netPayableAmount: data.netPayableAmount,
          tdsSection: data.tdsSection,
          tdsRate: data.tdsRate,
          tdsDeducted: data.tdsDeducted,
          otherDeductions: data.otherDeductions,
          utrReference: data.utrNumber,
          narration: data.narration,
          remarks: data.remarks,

          // Process payment lines (allocations)
          allocations:
            data.raBillPaymentLines?.map((line: any) => ({
              id: line.raBillPaymentLineId,
              raBillId: line.raBillId,
              raBillNumber: line.raBillSequenceId,
              workOrderId: line.workOrderId,
              workOrderNumber: line.workOrderSequenceId || line.workOrderNumber,
              thisPaymentAmount: line.paidAmount,
              resultingStatus: line.status,
            })) || [],

          // Process approval history
          paymentApprovals:
            data.paymentApprovals?.map((approval: any) => ({
              ...approval,
              level: approval.level?.toLowerCase() || approval.level,
              status: approval.status?.toLowerCase() || approval.status
            })) || [],

          approvalHistory:
            data.paymentApprovals?.map((approval: any) => ({
              action:
                approval.status === "Approved"
                  ? "Approved"
                  : approval.status === "Rejected"
                    ? "Rejected"
                    : "Pending",
              actionBy: approval.approvedByName || approval.approverName,
              actionDate: approval.approvalDate || approval.createdAt,
              role: approval.approvalStage,
              status: approval.status,
              comments: approval.reason,
              level: approval.level,
            })) || [],

          // Process attachments/documents
          attachments: data.dms || [],

          // Additional details
          createdBy: data.createdByName,
          createdAt: data.createdAt,
          processedBy: data.processedByName,
          processedAt: data.processedAt,
          approvedBy: data.approvedByName,
          approvedAt: data.approvedAt,
        };

        const isStrict =
          data?.isStrictApproval === true || data?.isStrictApproval === 1;
        setIsStrictMode(isStrict);

        if (data.paymentApprovals) {
          let ids = data.paymentApprovals
            ?.filter((item: any) => item?.status?.toLowerCase() !== "approved")
            ?.map((item: any) => item?.userId);
          setApproverIds(ids);
        }

        setSelectedPayment(processedPayment);
        setShowDetailsDrawer(true);
      } else {
        Swal.fire(
          "Error!",
          message || "Failed to fetch payment details",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error fetching payment details:", error);
      Swal.fire(
        "Error!",
        "Failed to fetch payment details. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle opening payment modal from notification navigation
  useEffect(() => {
    const paymentId = location?.state?.id;
    if (paymentId) {
      // Try to find payment in the list first
      const payment = payments.find((p) => p.id === paymentId);

      // Create payment object with raBillPaymentId for API call
      const paymentForView = payment
        ? {
          ...payment,
          raBillPaymentId: paymentId,
        }
        : {
          id: paymentId,
          raBillPaymentId: paymentId,
        };

      // Open modal directly with API call (don't wait for payments list)
      handleViewPayment(paymentForView as any);

      // Clear the state to prevent reopening on re-render
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.id]);

  const loadPaymentsForApproval = async () => {
    try {
      setLoading(true);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get payments pending approval for current user role
      const pendingPayments = getPaymentsForApproval(currentUserRole);
      setPayments(pendingPayments);
    } catch (error) {
      console.error("Error loading payments:", error);
      Swal.fire("Error!", "Failed to load payments", "error");
    } finally {
      setLoading(false);
    }
  };

  const parseLevelNum = (level: any) => {
    if (!level && level !== 0) return NaN;
    const str = String(level).toLowerCase();

    if (str.includes('level')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    } else if (str.startsWith('l')) {
      const match = str.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    }
    return NaN;
  };

  const hasUserAlreadyApproved = selectedPayment?.paymentApprovals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    // 1. Admin can Approve/Reject anytime - Buttons are always ENABLED
    if (currentUser?.genericRole?.toLowerCase() === "admin") {
      return true;
    }

    const approvals = selectedPayment?.paymentApprovals || [];

    // Find current user's approval entry
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUserId
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") {
      return false;
    }

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);

    // 2. Strict = 0 (Flexible mode)
    // Anyone in the approval list can approve at any time
    if (isStrictMode === false) {
      // In flexible mode, just check if user is in the approval list and pending
      return true;
    }

    // 3. Strict = 1 (Sequential mode)
    // Must follow the order. Level 2 cannot see buttons until Level 1 is "Approved"
    if (isStrictMode === true) {
      // Get all pending approvals sorted by level
      const pendingApprovals = approvals
        .filter((approval: any) => approval.status?.toLowerCase() === "pending")
        .sort((a: any, b: any) => {
          const aNum = parseLevelNum(a.level);
          const bNum = parseLevelNum(b.level);
          if (isNaN(aNum) || isNaN(bNum)) return 0;
          return aNum - bNum;
        });

      // If there are no pending approvals, user can't approve
      if (pendingApprovals.length === 0) return false;

      // Get the first pending approval (lowest level)
      const firstPending = pendingApprovals[0];
      const firstPendingLevelNum = parseLevelNum(firstPending.level);

      // Check if user is the first pending approver
      return userApproval.userId === firstPending.userId &&
        userLevelNum === firstPendingLevelNum;
    }

    return false;
  }, [currentUser, currentUserId, selectedPayment?.paymentApprovals, isStrictMode]);

  const getPendingStatusMessage = () => {
    const approvals = selectedPayment?.paymentApprovals || [];
    const pendingApprovals = approvals
      .filter((approval: any) => approval.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => {
        const aNum = parseLevelNum(a.level);
        const bNum = parseLevelNum(b.level);
        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return aNum - bNum;
      });

    if (pendingApprovals?.length > 0) {
      if (!isStrictMode) {
        // Flexible mode - any approver can approve
        const approverNames = pendingApprovals.map((approval: any) => approval.userName).join(", ");
        return `Status is pending. Any of these approvers can approve: ${approverNames}`;
      } else {
        // Strict mode - must follow sequence
        const nextApprover = pendingApprovals[0];
        const lvl = parseLevelNum(nextApprover.level);
        const levelText = !isNaN(lvl) ? `Level ${lvl}` : nextApprover.roleName || "";
        return `Status is pending. Waiting for ${nextApprover.userName} (${levelText}) to approve first.`;
      }
    }

    return "Status is pending by other approvers";
  };

  const handleApprovalAction = (payment: any, action: "approve" | "reject") => {
    setSelectedPayment(payment);
    setApprovalAction(action);

    if (action === "approve") {
      setActiveSignatureContext("APPROVE");
      setIsModalApproveVisible(true);
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    } else {
      setActiveSignatureContext("REJECT");
      setIsModalRejectedVisible(true);
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
      form.resetFields();
    }
  };

  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  // const handleApprove = async (files: File[] = []) => {
  //   try {
  //     setActionLoading(true);

  //     // Use the existing approvalAction API function
  //     const response: any = await paymentApis.approvalAction({
  //       payment: {
  //         raBillPaymentId: selectedPayment?.raBillPaymentId || selectedPayment?.id,
  //         action: "Approved",
  //         reason: "Payment approved"
  //       },
  //       files: files
  //     });

  //     if (response.status) {
  //       Swal.fire("Success!", "Payment approved successfully", "success");

  //       // Remove from pending list and refresh data
  //       setPayments((prev) => prev.filter((p) => p.id !== selectedPayment.id));
  //       setRefreshToggle(!refreshToggle);
  //       setShowDetailsDrawer(false);
  //     } else {
  //       throw new Error(response.message || "Failed to approve payment");
  //     }
  //   } catch (error: any) {
  //     console.error("Error approving payment:", error);
  //     Swal.fire(
  //       "Error!",
  //       error.message || "Failed to approve payment",
  //       "error"
  //     );
  //   } finally {
  //     setActionLoading(false);
  //     setSignature({ isActive: false, label: "Authorised Signatory", image: null });
  //     setIsModalApproveVisible(false);
  //   }
  // };

  // const handleReject = async (reason: string, files: File[] = []) => {
  //   try {
  //     setActionLoading(true);

  //     // Use the existing approvalAction API function
  //     const response: any = await paymentApis.approvalAction({
  //       payment: {
  //         raBillPaymentId: selectedPayment?.raBillPaymentId || selectedPayment?.id,
  //         action: "Rejected",
  //         reason: reason
  //       },
  //       files: files
  //     });

  //     if (response.status) {
  //       Swal.fire("Success!", "Payment rejected successfully", "success");

  //       // Remove from pending list and refresh data
  //       setPayments((prev) => prev.filter((p) => p.id !== selectedPayment.id));
  //       setRefreshToggle(!refreshToggle);
  //       setShowDetailsDrawer(false);
  //     } else {
  //       throw new Error(response.message || "Failed to reject payment");
  //     }
  //   } catch (error: any) {
  //     console.error("Error rejecting payment:", error);
  //     Swal.fire(
  //       "Error!",
  //       error.message || "Failed to reject payment",
  //       "error"
  //     );
  //   } finally {
  //     setActionLoading(false);
  //     setSignature({ isActive: false, label: "Authorised Signatory", image: null });
  //     setIsModalRejectedVisible(false);
  //     form.resetFields();
  //   }
  // };

  const handleApprove = async (files: File[] = []) => {
    try {
      setActionLoading(true);

      const response: any = await paymentApis.approvalAction({
        raBillPaymentId: selectedPayment?.raBillPaymentId || selectedPayment?.id,
        action: "Approved",
        reason: "Payment approved"
      }, files);

      if (response.status) {
        Swal.fire("Success!", "Payment approved successfully", "success");
      } else {
        throw new Error(response.message || "Failed to approve payment");
      }
    } catch (error: any) {
      console.error("Error approving payment:", error);
      Swal.fire("Error!", error.message || "Failed to approve payment", "error");
    } finally {
      setActionLoading(false);
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
      setIsModalApproveVisible(false);
    }
  };

  const handleReject = async (reason: string, files: File[] = []) => {
    try {
      setActionLoading(true);

      const response: any = await paymentApis.approvalAction({
        raBillPaymentId: selectedPayment?.raBillPaymentId || selectedPayment?.id,
        action: "Rejected",
        reason: reason
      }, files);

      if (response.status) {
        Swal.fire("Success!", "Payment rejected successfully", "success");
      } else {
        throw new Error(response.message || "Failed to reject payment");
      }
    } catch (error: any) {
      console.error("Error rejecting payment:", error);
      Swal.fire("Error!", error.message || "Failed to reject payment", "error");
    } finally {
      setActionLoading(false);
      setSignature({ isActive: false, label: "Authorised Signatory", image: null });
      setIsModalRejectedVisible(false);
      form.resetFields();
    }
  };

  const openApproveConfirmSwal = () => {
    if (!signature.image) {
      Swal.fire({
        icon: "info",
        title: "Signature Required",
        text: "Please add your signature before approving.",
      });
      return;
    }

    setIsModalApproveVisible(false);

    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const file = dataURLToFile(signature.image as string, "signature.png");
        await handleApprove([file]);
      } else {
        setIsModalApproveVisible(true);
      }
    });
  };

  const openRejectConfirmSwal = async () => {
    try {
      await form.validateFields();

      if (!signature.image) {
        Swal.fire({
          icon: "info",
          title: "Signature Required",
          text: "Please add your signature before rejecting.",
        });
        return;
      }

      setIsModalRejectedVisible(false);

      Swal.fire({
        title: "Confirm Rejection?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Reject it!",
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const values = form.getFieldsValue();
          const file = dataURLToFile(signature.image as string, "signature.png");
          await handleReject(values.reason, [file]);
        } else {
          setIsModalRejectedVisible(true);
        }
      });
    } catch (e) {
      console.log("Validation failed:", e);
    }
  };

  const handleCancel = () => {
    setIsModalRejectedVisible(false);
    form.resetFields();
  }

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...advancedFilters });
    filterForm.setFieldsValue(advancedFilters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const flowTypeParam = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const partyType = isClientFlow ? "Client" : "Contractor";
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || "N/A",
          value: item.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch work orders
      const workOrdersResponse = await dropdownApis.fetchWorkOrderDropdown(
        flowTypeParam
      );
      if (
        workOrdersResponse &&
        workOrdersResponse.status &&
        workOrdersResponse.data
      ) {
        const workOrderOptions = workOrdersResponse.data.map((item: any) => {
          const label =
            item.workOrderNumber ||
            item.workOrderSequenceId ||
            item.sequenceId ||
            item.workOrderTitle ||
            `WO-${item.workOrderId || item.id || "N/A"}`;
          const value = item.workOrderId?.toString() || item.id?.toString();
          return { label, value };
        });
        setWorkOrderOptions(workOrderOptions);
      }

      // Fetch RA Bills
      const raBillsResponse = await dropdownApis.fetchRABillDropdown(
        flowTypeParam
      );
      if (raBillsResponse && raBillsResponse.status && raBillsResponse.data) {
        const raBillOptions = raBillsResponse.data.map((item: any) => ({
          label: item.raBillSequenceId || `RA-${item.raBillId || "N/A"}`,
          value: item.raBillId?.toString(),
        }));
        setRABillOptions(raBillOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleApplyFilters = () => {
    filterForm
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          workOrderId:
            values.workOrderId && values.workOrderId.length > 0
              ? values.workOrderId
              : undefined,
          raBillId:
            values.raBillId && values.raBillId.length > 0
              ? values.raBillId
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Always create a new object reference to trigger useEffect
        setAdvancedFilters({ ...processedFilters });
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setAdvancedFilters({});
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName]: { ...advancedFilters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setAdvancedFilters(savedFilter);
      filterForm.setFieldsValue(savedFilter);
    }
  };

  const getStatusColor = (status: PaymentStatus): string => {
    const colors: Record<PaymentStatus, string> = {
      [PaymentStatus.Draft]: "default",
      [PaymentStatus.Submitted]: "processing",
      [PaymentStatus.Checked]: "warning",
      [PaymentStatus.Verified]: "orange",
      [PaymentStatus.Approved]: "success",
      [PaymentStatus.Posted]: "cyan",
      [PaymentStatus.Paid]: "green",
      [PaymentStatus.Rejected]: "error",
      [PaymentStatus.Returned]: "purple",
    };
    return colors[status] || "default";
  };

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Table columns for payment approvals
  const paymentColumns: TableColumnsType<PaymentData> = [
    {
      title: "Payment Voucher",
      dataIndex: "raBillPaymentSequenceId",
      key: "raBillPaymentSequenceId",
      width: 230,
      render: (text: string) => (
        <div className="fw-bold text-primary">{text}</div>
      ),
    },
    {
      title: flowType?.toLowerCase() === "client" ? "Client" : "Contractor",
      key: "contractor",
      width: 250,
      render: (record: PaymentData) => (
        <div>
          <div className="fw-bold">{record.contractorName}</div>
          <small className="text-muted">{record.siteName}</small>
        </div>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    {
      title: "Submitted",
      key: "submitted",
      width: 120,
      render: (record: PaymentData) => (
        <div>
          <div>{dayjs(record.submittedAt).format("DD-MMM-YYYY")}</div>
          <small className="text-muted">{record.submittedBy}</small>
        </div>
      ),
    },
    {
      title: "Net Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 120,
      render: (amount: number) => (
        <div className="fw-bold text-success">₹{amount?.toLocaleString()}</div>
      ),
    },
    {
      title: "Reference #",
      dataIndex: "utrNumber",
      key: "utrNumber",
      width: 140,
      render: (utr: string, record: any) => {
        const reference =
          record.utrNumber ||
          record.transactionNumber ||
          record.chequeNumber ||
          "N/A";
        return (
          <div>
            <div className="fw-bold">{reference}</div>
            <small className="text-muted">{record.paymentMethod} Payment</small>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: PaymentStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      fixed: "right",
      render: (record: PaymentData) => {
        const canApprove =
          record.status === PaymentStatus.Submitted ||
          record.status === PaymentStatus.Checked ||
          record.status === PaymentStatus.Verified;

        return (
          <Space>
            <Tooltip title="View Details">
              <AntButton
                type="text"
                icon={<EyeOutlined />}
                onClick={() => handleViewPayment(record)}
              />
            </Tooltip>

            {canApprove && (
              <>
                <Tooltip title="Approve">
                  <AntButton
                    type="text"
                    icon={<CheckOutlined style={{ color: "#52c41a" }} />}
                    onClick={() => handleApprovalAction(record, "approve")}
                  />
                </Tooltip>
                <Tooltip title="Reject">
                  <AntButton
                    type="text"
                    icon={<CloseOutlined style={{ color: "#ff4d4f" }} />}
                    onClick={() => handleApprovalAction(record, "reject")}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="view-payments-pending"
        title="Pending Payments"
        columns={paymentColumns}
        loading={loading}
        customFilters={advancedFilters}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <AntButton
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </AntButton>
            </Badge>
          </Space>
        }
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${sm ? "content-active" : ""
              }`}
          >
            {flowType?.toLowerCase() === "client" ? (
              <ReceivablesAside updateSm={setSm} sm={sm} />
            ) : (
              <PaymentsAside updateSm={setSm} sm={sm} />
            )}
          </div>
        }
        extra={{
          tableScrollX: 1200,
        }}
      />

      {/* Payment Details Drawer */}
      <Drawer
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Payment Details - {selectedPayment?.paymentVoucherNumber}</strong>
            <div style={{ textAlign: "right" }}>
              {selectedPayment?.status === "Submitted" || selectedPayment?.status === "Pending" ?
                !hasUserAlreadyApproved && (
                  <>
                    {canUserApproveReject() ? (
                      <>
                        <Button
                          onClick={() => {
                            setApprovalAction("approve");
                            setActiveSignatureContext("APPROVE");
                            setIsModalApproveVisible(true);
                          }}
                          color="success"
                          className="m-3 mt-0 mb-0"
                        >
                          <Icon name="check" /> Approve
                        </Button>
                        <Button
                          onClick={() => {
                            setApprovalAction("reject");
                            setActiveSignatureContext("REJECT");
                            setIsModalRejectedVisible(true);
                            form.resetFields();
                          }}
                          color="warning"
                          className="m-3 mt-0 mb-0"
                        >
                          <Icon name="cross" /> Reject
                        </Button>
                      </>
                    ) : (
                      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                        <Button
                          color="secondary"
                          className="m-3 mt-0 mb-0"
                          disabled
                          style={{ cursor: "not-allowed", opacity: 0.6 }}
                        >
                          <Icon name="check" /> Approve
                        </Button>
                        <Button
                          color="secondary"
                          className="m-3 mt-0 mb-0"
                          disabled
                          style={{ cursor: "not-allowed", opacity: 0.6 }}
                        >
                          <Icon name="cross" /> Reject
                        </Button>
                        <Popover
                          content={
                            <div style={{ padding: "8px", maxWidth: "300px" }}>
                              <p style={{ margin: 0 }}>{getPendingStatusMessage()}</p>
                            </div>
                          }
                          title="Approval Status"
                          trigger="hover"
                        >
                          <InfoCircleOutlined
                            style={{
                              fontSize: "20px",
                              cursor: "pointer"
                            }}
                          />
                        </Popover>
                      </div>
                    )}
                  </>
                ) : null}
            </div>
          </div>
        }
        placement="right"
        onClose={() => setShowDetailsDrawer(false)}
        open={showDetailsDrawer}
        width="85vw"
        style={{
          position: "absolute",
          zIndex: 1100,
        }}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {selectedPayment ? (
          <>
            <div className="d-flex justify-content-between p-3 pb-0 pt-0">
              <div>
                <strong>Remark</strong>
                <p>{selectedPayment?.remarks || "No remarks"}</p>
              </div>
              <div>
                <div className="w-100">
                  <div className="d-flex justify-content-between">
                    <div className="justify-end">
                      <strong>Payable Amount Total &nbsp; &nbsp; </strong>
                    </div>
                    <strong className="fw-bold">
                      ₹{selectedPayment?.netPayableAmount?.toLocaleString() || "0"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {selectedPayment.allocations && selectedPayment.allocations.length > 0 && (
              <>
                <div className="table-responsive poTable">
                  <div
                    style={{
                      width: screenSize - 300 + "px",
                      margin: "0px auto 15px auto",
                    }}
                  >
                    <Table
                      className="customTable"
                      columns={[
                        {
                          title: "#",
                          key: "#",
                          width: 50,
                          render: (text: any, record: any, index: any) => index + 1,
                        },
                        {
                          title: "RA Bill #",
                          dataIndex: "raBillNumber",
                          key: "raBillNumber",
                          width: 140,
                        },
                        {
                          title: "Work Order #",
                          dataIndex: "workOrderNumber",
                          key: "workOrderNumber",
                          width: 140,
                        },
                        {
                          title: "Payment Amount",
                          dataIndex: "thisPaymentAmount",
                          key: "thisPaymentAmount",
                          width: 140,
                          render: (amount: number) => `₹${amount?.toLocaleString()}`,
                        },
                        {
                          title: "Resulting Status",
                          dataIndex: "resultingStatus",
                          key: "resultingStatus",
                          width: 140,
                          render: (status: string) => {
                            const color =
                              status === "Paid"
                                ? "green"
                                : status === "Partially Paid"
                                  ? "orange"
                                  : "red";
                            return <Tag color={color}>{status}</Tag>;
                          },
                        },
                      ]}
                      dataSource={selectedPayment.allocations}
                      scroll={{ x: 1000 }}
                      bordered
                      size="middle"
                      pagination={false}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Approval History */}
            {selectedPayment.approvalHistory && selectedPayment.approvalHistory.length > 0 && (
              <>
                <Divider />
                <h6>Approval History</h6>
                <Timeline>
                  {selectedPayment.approvalHistory?.map(
                    (history: any, index: any) => (
                      <Timeline.Item
                        key={index}
                        color={history.status === "Rejected" ? "red" : "blue"}
                      >
                        <div>
                          <strong>{history.action}</strong> by {history.actionBy}
                          <div className="text-muted small">
                            {dayjs(history.actionDate).format("DD/MM/YYYY HH:mm")} |{" "}
                            {history.role}
                          </div>
                          {history.comments && (
                            <div className="mt-1 p-2 bg-light rounded">
                              <small>{history.comments}</small>
                            </div>
                          )}
                        </div>
                      </Timeline.Item>
                    )
                  )}
                </Timeline>
              </>
            )}
          </>
        ) : (
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        )}
      </Drawer>

      {/* Advanced Filters Drawer */}
      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        placement="right"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <AntButton size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <AntButton
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </AntButton>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Select.Option key={filterName} value={filterName}>
                      {filterName}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <AntButton
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </AntButton>
            </Space>
          </div>
        }
      >
        <Form
          form={filterForm}
          layout="vertical"
          initialValues={advancedFilters}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site/Project">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderId" label="Work Order Number">
                <Select
                  mode="multiple"
                  placeholder="Select Work Orders"
                  options={workOrderOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="raBillId" label="RA Bill">
                <Select
                  mode="multiple"
                  placeholder="Select RA Bills"
                  options={raBillOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      {/* Approve Modal with Signature */}
      <Modal
        title="Approve Payment"
        visible={isModalApproveVisible}
        onOk={() => {
          if (!signature.image) {
            Swal.fire({
              icon: "info",
              title: "Signature Required",
              text: "Please add your signature before approving.",
            });
            return;
          }
          openApproveConfirmSwal();
        }}
        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>
            {signature.image && (
              <button
                type="button"
                onClick={() =>
                  setSignature({
                    isActive: true,
                    label: "Authorised Signatory",
                    image: null,
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: "pointer",
                }}
              >
              </button>
            )}
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                }}
              >
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                />
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  onClick={() =>
                    setSignature({
                      isActive: true,
                      label: "Authorised Signatory",
                      image: null,
                    })
                  }
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                width: "100%",
                minHeight: 220,
                border: "2px dashed #dee2e6",
                borderRadius: 8,
                backgroundColor: "#f8f9fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  style={{ minWidth: 140 }}
                  onClick={() => {
                    setIsModalApproveVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Reject Modal with Signature */}
      <Modal
        title="Reject Payment"
        visible={isModalRejectedVisible}
        onOk={openRejectConfirmSwal}
        onCancel={() => {
          handleCancel();
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image && (
            <a
              style={{ fontSize: 14, cursor: "pointer" }}
              onClick={() =>
                setSignature({ isActive: true, label: "Authorised Signatory", image: null })
              }
            >
            </a>
          )}

          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img
                  src={signature.image}
                  alt="Signed"
                  style={{ width: "180px", height: "auto" }}
                />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button
                  size="sm"
                  color="outline-secondary"
                  className="me-2"
                  onClick={() =>
                    setSignature({ isActive: true, label: "Authorised Signatory", image: null })
                  }
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div
              style={{
                minHeight: "200px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "15px",
                border: "2px dashed #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa"
              }}
            >
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => {
                    setSignature((prev: any) => ({ ...prev, image: data }));
                  }}
                >
                  <Button color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>

                <Button
                  color="outline-primary"
                  size="sm"
                  onClick={() => {
                    setIsModalRejectedVisible(false);
                    setShowSignatureModel(true);
                  }}
                >
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {showSignatureModel && (
        <SignatureModal
          modal={showSignatureModel}
          toggle={() => {
            setShowSignatureModel(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
          onSave={(data: string) => {
            setSignature((prev: any) => ({ ...prev, image: data }));
            setShowSignatureModel(false);

            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
        />
      )}
    </>
  );
};
export default PendingPaymentApprovals;